import { useEffect, useId, useRef, useState } from "react";

import { clearAuthSession } from "@/lib/auth/session";
import { getGlofoxDocsHomeHref } from "@/lib/portal/docs-urls";

interface UserAccountMenuProps {
  userName: string;
  testIdPrefix: string;
  variant?: "home" | "portal";
}

export function UserAccountMenu({
  userName,
  testIdPrefix,
  variant = "home",
}: UserAccountMenuProps) {
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const isPortal = variant === "portal";
  const displayName = userName.trim().split(/\s+/)[0] || "Developer";
  const nameColor = isPortal ? "rgba(255,255,255,.7)" : "var(--t1)";
  const avatarBg = isPortal ? "var(--gf)" : "var(--abc)";

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  function handleLogout() {
    clearAuthSession();
    setOpen(false);
    window.location.assign(getGlofoxDocsHomeHref());
  }

  return (
    <div
      ref={containerRef}
      className={isPortal ? "user-menu user-menu--portal" : "user-menu"}
      style={{ position: "relative" }}
      data-testid={`${testIdPrefix}-user`}
    >
      <button
        type="button"
        className={`user-menu-trigger${
          isPortal ? " user-menu-trigger--portal" : ""
        }`}
        data-testid={`${testIdPrefix}-user-name`}
        aria-label={isPortal ? `${displayName} account menu` : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: "var(--font)",
        }}
      >
        <div
          className="user-menu-avatar"
          data-testid={`${testIdPrefix}-user-avatar`}
          style={{
            width: 30,
            height: 30,
            background: avatarBg,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 800,
            color: "#fff",
          }}
          aria-hidden
        >
          {displayName.charAt(0).toUpperCase() || "?"}
        </div>
        {!isPortal ? (
          <>
            <span
              style={{
                color: nameColor,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {userName}
            </span>
            <i
              className={`ti ti-chevron-${open ? "up" : "down"}`}
              style={{
                fontSize: 14,
                color: "var(--t3)",
              }}
              aria-hidden
            />
          </>
        ) : null}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className={`user-menu-dropdown${
            isPortal ? " user-menu-dropdown--portal" : ""
          }`}
          data-testid={`${testIdPrefix}-user-menu`}
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            minWidth: 140,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            padding: 6,
            zIndex: 300,
          }}
        >
          {isPortal ? (
            <p className="user-menu-dropdown__name">{displayName}</p>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="user-menu-item"
            data-testid={`${testIdPrefix}-logout`}
            onClick={handleLogout}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              border: "none",
              borderRadius: 8,
              background: "transparent",
              color: "var(--t1)",
              fontFamily: "var(--font)",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <i className="ti ti-logout" aria-hidden />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
